
import { useState, useEffect } from 'react';

interface CartItem {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  quantity: number;
  calories: number;
}

export default function CartPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Existing cart items state and functions
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  /** Load cart items from localStorage safely */
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('mangoTreasureCart');
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) {
          setCartItems(parsed);
        }
      }
    } catch (error) {
      console.error('Error reading cart from localStorage:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Update the quantity of an item; remove if quantity becomes 0 */
  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(id);
      return;
    }

    const updatedItems = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedItems);
    try {
      localStorage.setItem('mangoTreasureCart', JSON.stringify(updatedItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  /** Remove a single item from the cart */
  const removeItem = (id: number) => {
    const updatedItems = cartItems.filter(item => item.id !== id);
    setCartItems(updatedItems);
    try {
      localStorage.setItem('mangoTreasureCart', JSON.stringify(updatedItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  /** Empty the entire cart */
  const clearCart = () => {
    setCartItems([]);
    try {
      localStorage.removeItem('mangoTreasureCart');
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  /** Pricing calculations */
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 50 ? 0 : 6.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  /** Loading state */
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-purple-600">Loading cart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50">
      {/* Free Shipping Banner */}
      <section className="bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-truck-line text-2xl"></i>
            <span className="text-lg font-semibold">
              FREE SHIPPING on orders over $50!
            </span>
            <i className="ri-gift-line text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <nav className="bg-gradient-to-r from-rose-100/95 via-pink-100/95 to-orange-100/95 backdrop-blur-md shadow-xl sticky top-0 z-50 border-b border-rose-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                alt="Mango Treasure Logo"
                className="h-14 w-auto mr-4 drop-shadow-sm"
              />
              <span
                className="text-3xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent"
                style={{ fontFamily: '"Pacifico", serif' }}
              >
                Mango Treasure
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 px-4 py-3 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg flex items-center space-x-2 font-semibold"
                  href="/cart"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-rose-600 p-3 rounded-lg shadow-lg transition-all duration-300 cursor-pointer border border-rose-100"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur-md rounded-lg mt-2 shadow-xl border border-rose-200/50">
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/products"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Shop Products
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/story"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Our Story
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <a
                  className="text-gray-700 hover:text-rose-700 block px-3 py-2 text-base font-semibold transition-all duration-300 cursor-pointer rounded-lg hover:bg-rose-200/50"
                  href="/orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Track Orders
                </a>
                <a
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white px-6 py-3 rounded-full hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 cursor-pointer shadow-lg flex items-center space-x-2 font-semibold justify-center mt-4"
                  href="/cart"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  <span>Cart</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6 drop-shadow-lg">Shopping Cart</h1>
          <p className="text-xl text-rose-100 max-w-3xl mx-auto drop-shadow-md">
            Review your selected items and proceed to checkout
          </p>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shopping-cart-line text-purple-500 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-purple-800 mb-4">Your cart is empty</h2>
              <p className="text-purple-600 mb-8">Add some delicious mango puddings to get started!</p>
              <a
                href="/products"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                Shop Products
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-rose-100/50">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent">Cart Items</h2>
                    <button
                      onClick={clearCart}
                      className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors cursor-pointer"
                    >
                      Clear Cart
                    </button>
                  </div>

                  <div className="space-y-6">
                    {cartItems.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-4 p-4 border border-rose-100 rounded-xl bg-white/80 backdrop-blur-sm"
                      >
                        <img src={item.image} alt={item.name} className="w-20 h-20 object-cover object-top rounded-lg shadow-lg" />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-rose-800">{item.name}</h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">${item.price}</span>
                            {item.originalPrice && (
                              <span className="text-sm text-gray-400 line-through">
                                ${item.originalPrice}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-green-600 text-sm mt-1">
                            <i className="ri-leaf-line mr-1"></i>
                            <span>{item.calories} cal</span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-600 rounded-full flex items-center justify-center hover:from-rose-200 hover:to-pink-200 transition-colors cursor-pointer shadow-lg"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-8 text-center font-semibold text-rose-800">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-600 rounded-full flex items-center justify-center hover:from-rose-200 hover:to-pink-200 transition-colors cursor-pointer shadow-lg"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>

                        <div className="text-right">
                          <p className="text-lg font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-red-500 hover:text-red-700 text-sm mt-1 transition-colors cursor-pointer"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-rose-100/50 sticky top-32">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-rose-700 via-pink-700 to-orange-700 bg-clip-text text-transparent mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold text-rose-800">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping</span>
                      <span className="font-semibold text-rose-800">
                        {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax</span>
                      <span className="font-semibold text-rose-800">${tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-rose-200 pt-4">
                      <div className="flex justify-between">
                        <span className="text-lg font-bold text-rose-800">Total</span>
                        <span className="text-lg font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
                          ${total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {subtotal < 50 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <i className="ri-information-line text-blue-500 mr-2"></i>
                        <span className="text-sm text-blue-700">
                          Add ${(50 - subtotal).toFixed(2)} more for free shipping!
                        </span>
                      </div>
                    </div>
                  )}

                  <button className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500 text-white py-4 rounded-xl font-semibold hover:from-rose-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
                    Proceed to Checkout
                  </button>

                  <div className="mt-4 text-center">
                    <a
                      href="/products"
                      className="text-rose-600 hover:text-rose-700 text-sm font-medium transition-colors cursor-pointer"
                    >
                      Continue Shopping
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-rose-900 via-pink-900 to-orange-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img
                  src="https://static.readdy.ai/image/e97325bca996f1ce3b54a2c00c06237a/b38163c3c482e773cae655e9f9113361.png"
                  alt="Mango Treasure Logo"
                  className="h-12 w-auto mr-4"
                />
                <h3 className="text-2xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                  Mango Treasure
                </h3>
              </div>
              <p className="text-rose-200 mb-6 max-w-md">
                Bringing you the finest mango puddings with authentic flavors and premium quality ingredients.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-rose-800 rounded-full flex items-center justify-center hover:bg-rose-700 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-rose-800 rounded-full flex items-center justify-center hover:bg-rose-700 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-rose-800 rounded-full flex items-center justify-center hover:bg-rose-700 transition-colors cursor-pointer">
                  <i className="ri-twitter-line"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Home</a></li>
                <li><a href="/products" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Products</a></li>
                <li><a href="/story" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Our Story</a></li>
                <li><a href="/contact" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Customer Service</h4>
              <ul className="space-y-2">
                <li><a href="/orders" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Track Orders</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Shipping Info</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">Returns</a></li>
                <li><a href="#" className="text-rose-200 hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-rose-800 mt-12 pt-8 text-center">
            <p className="text-rose-200">
              © 2024 Mango Treasure. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-rose-300 hover:text-white transition-colors cursor-pointer ml-1">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
